import { useCallback, useEffect, useRef } from "react";

import { subscribeToAnalysisResult, type AnalysisErrorReason } from "../api/analysisSubscription";
import type { AnalyticsStatus, RecordingDetails } from "../api/types";

type OnComplete = (data: RecordingDetails) => void;
type OnError = (message: string, reason: AnalysisErrorReason) => void;
type OnStatus = (status: AnalyticsStatus) => void;

type UseAnalysisSubscriptionsReturn = {
  subscribe: (id: string, onComplete: OnComplete, onError: OnError, onStatus?: OnStatus) => void;
  unsubscribe: (id: string) => void;
};

export const useAnalysisSubscriptions = (): UseAnalysisSubscriptionsReturn => {
  const cleanups = useRef<Record<string, () => void>>({});

  useEffect(() => {
    const current = cleanups.current;
    return () => {
      for (const cleanup of Object.values(current)) cleanup();
    };
  }, []);

  const subscribe = useCallback<UseAnalysisSubscriptionsReturn["subscribe"]>(
    (id, onComplete, onError, onStatus) => {
      if (cleanups.current[id]) return;
      cleanups.current[id] = subscribeToAnalysisResult(
        id,
        data => {
          delete cleanups.current[id];
          onComplete(data);
        },
        (message, reason) => {
          delete cleanups.current[id];
          onError(message, reason);
        },
        onStatus,
      );
    },
    [],
  );

  const unsubscribe = useCallback<UseAnalysisSubscriptionsReturn["unsubscribe"]>(id => {
    cleanups.current[id]?.();
    delete cleanups.current[id];
  }, []);

  return { subscribe, unsubscribe };
};
